import React, { ReactElement, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Container, Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

interface ProtectedRouteProps {
  children: ReactElement;
  fallback?: ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback 
}) => {
  const { isAuthenticated, isLoading, login, error } = useAuth();
  const navigate = useNavigate();
  const [loginTriggered, setLoginTriggered] = React.useState(false);
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (hasCheckedAuth && !isAuthenticated && !error && !loginTriggered) {
      setLoginTriggered(true);
      login().catch((err) => {
        console.error('Auto-login failed:', err);
        navigate(ROUTES.HOME);
      });
    }
  }, [hasCheckedAuth, isAuthenticated, error, loginTriggered, login, navigate]);

  if (isLoading) {
    return (
      <Container className="mt-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted">Checking authentication...</p>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Authentication Error
          </Alert.Heading>
          <p className="mb-3">{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-danger"
              onClick={() => window.location.reload()}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Retry
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!isAuthenticated) {
    if (fallback) {
      return fallback;
    }

    return (
      <Container className="mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            </div>
            <h4 className="mb-3">
              <i className="bi bi-shield-lock me-2"></i>
              Authentication Required
            </h4>
            <p className="text-muted mb-4">
              Redirecting you to the sign-in page...
            </p>
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <small>Please complete the sign-in process in the popup window</small>
            </div>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate(ROUTES.HOME)}
            >
              <i className="bi bi-house me-2"></i>
              Return to Home
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return children;
};

export default ProtectedRoute;
